import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';
import { extractTextFromBoilerGraphImage } from './boiler-graph-image-ocr.util';

/** File picker: images, PDFs, JSON, CSV, Excel, plain text (incl. screenshots). */
export const CARGO_TABLE_FILE_ACCEPT =
  'image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp,.heic,.heif,.tif,.tiff,.pdf,application/pdf,.csv,.json,.txt,.xlsx,.xls,text/plain,text/csv,application/json,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const CARGO_GRAPH_FILE_ACCEPT =
  'image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp,.heic,.heif,.pdf,application/pdf,.json,.txt,application/json';

export type CargoUploadKind = 'pdf' | 'image' | 'structured' | 'spreadsheet' | 'unknown';

declare global {
  interface Window {
    pdfjsLib?: {
      GlobalWorkerOptions: { workerSrc: string };
      getDocument: (src: { data: ArrayBuffer }) => {
        promise: Promise<{
          numPages: number;
          getPage: (n: number) => Promise<PdfPage>;
        }>;
      };
    };
  }
}

interface PdfPage {
  getTextContent: () => Promise<{
    items: Array<{ str?: string }>;
  }>;
  getViewport: (opts: { scale: number }) => { width: number; height: number };
  render: (ctx: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
}

let pdfJsInitialized = false;

function loadPdfJs(): Promise<void> {
  if (!pdfJsInitialized) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
    window.pdfjsLib = pdfjsLib as unknown as NonNullable<typeof window.pdfjsLib>;
    pdfJsInitialized = true;
  }
  return Promise.resolve();
}

/** True when blob begins with the PDF magic header (%PDF). */
export async function isPdfBlob(blob: Blob): Promise<boolean> {
  if (!blob?.size) {
    return false;
  }
  const type = blob.type?.toLowerCase() ?? '';
  if (type.includes('pdf')) {
    return true;
  }
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  return (
    head[0] === 0x25 &&
    head[1] === 0x50 &&
    head[2] === 0x44 &&
    head[3] === 0x46
  );
}

/** When the report API returns JSON/text instead of a PDF blob. */
export async function readBlobErrorMessage(blob: Blob): Promise<string | null> {
  if (await isPdfBlob(blob)) {
    return null;
  }
  try {
    const text = (await blob.text()).trim();
    if (!text) {
      return 'Empty report response from server.';
    }
    try {
      const parsed = JSON.parse(text);
      return (
        parsed?.message ||
        parsed?.error ||
        parsed?.detail ||
        'Could not download port consumption report.'
      );
    } catch {
      return text.slice(0, 240);
    }
  } catch {
    return 'Could not download port consumption report.';
  }
}

function resolvePdfRenderScale(
  page: PdfPage,
  preferredScale = 2,
  maxCanvasDim = 8192,
): number {
  const base = page.getViewport({ scale: 1 });
  const longestSide = Math.max(base.width, base.height);
  if (!longestSide) {
    return preferredScale;
  }
  const capped = maxCanvasDim / longestSide;
  return Math.min(preferredScale, Math.max(0.75, capped));
}

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export function isSpreadsheetFile(file: File): boolean {
  return (
    /\.(xlsx|xls)$/i.test(file.name) ||
    file.type.includes('spreadsheet') ||
    file.type.includes('excel')
  );
}

export function isStructuredTextFile(file: File): boolean {
  return (
    file.type.includes('text') ||
    file.type.includes('json') ||
    file.type.includes('csv') ||
    /\.(csv|json|txt|tsv)$/i.test(file.name)
  );
}

/** Images & screenshots (macOS may omit MIME type). */
export function isDocumentImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) {
    return true;
  }
  if (/\.(png|jpe?g|webp|gif|bmp|heic|heif|tif|tiff)$/i.test(file.name)) {
    return true;
  }
  if (/^screenshot|^image\s/i.test(file.name)) {
    return true;
  }
  return false;
}

export function classifyUploadFile(file: File): CargoUploadKind {
  if (isPdfFile(file)) {
    return 'pdf';
  }
  if (isDocumentImageFile(file)) {
    return 'image';
  }
  if (isSpreadsheetFile(file)) {
    return 'spreadsheet';
  }
  if (isStructuredTextFile(file)) {
    return 'structured';
  }
  return 'unknown';
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => resolve('');
    reader.readAsText(file);
  });
}

/** Per-page text extraction for port consumption multi-page reports. */
export async function extractTextByPageFromPdfBlob(
  blob: Blob,
): Promise<PdfPageText[]> {
  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    return [];
  }

  const buffer = await blob.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const pages: PdfPageText[] = [];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const lines = await extractLinesFromPdfPage(page);
    const rawText = lines.join('\n').trim();
    pages.push({ pageNumber: pageNum, lines, rawText });
  }

  return pages;
}

/** OCR only sparse consumption pages (2+). Skips cover and pages with text. */
async function ocrSparseDetailPagesOnly(
  file: File,
  pages: PdfPageText[],
): Promise<PdfPageText[]> {
  const enriched = [...pages];

  for (let i = 0; i < enriched.length; i++) {
    const page = enriched[i];
    if (page.pageNumber <= 1 || page.rawText.trim().length >= 15) {
      continue;
    }
    try {
      const pageImg = await pdfPageAsImageFile(file, page.pageNumber, false);
      const ocrText = (await extractTextFromBoilerGraphImage(pageImg)).trim();
      if (ocrText) {
        enriched[i] = {
          pageNumber: page.pageNumber,
          lines: ocrText.split('\n').map((l) => l.trim()).filter(Boolean),
          rawText: ocrText,
        };
      }
    } catch {
      /* keep original page text */
    }
  }

  return enriched;
}

export function truncateExtractedReportContext(
  text: string,
  maxChars = 10000,
): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxChars) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxChars)}\n\n[Report text truncated for analysis speed]`;
}

export function formatPortConsumptionPagesContext(pages: PdfPageText[]): string {
  return pages
    .map((page) => {
      const body = page.lines.length ? page.lines.join('\n') : page.rawText;
      return `--- PAGE ${page.pageNumber} ---\n${body}`.trim();
    })
    .join('\n\n');
}

export function validatePortConsumptionExtraction(pages: PdfPageText[]): {
  valid: boolean;
  error?: string;
  pagesIncluded: number[];
} {
  if (!pages.length) {
    return {
      valid: false,
      error: 'No readable content could be extracted from the PDF report.',
      pagesIncluded: [],
    };
  }

  const pagesIncluded = pages.map((p) => p.pageNumber);
  const fullText = pages.map((p) => p.rawText).join('\n');
  const detailPages = pages.length > 1 ? pages.slice(1) : pages;
  const detailText = detailPages.map((p) => p.rawText).join('\n');
  const detailChars = detailText.replace(/\s/g, '').length;
  const detailMarkerCount = PORT_CONSUMPTION_DETAIL_MARKERS.filter((rx) =>
    rx.test(detailText),
  ).length;
  const fullMarkerCount = PORT_CONSUMPTION_DETAIL_MARKERS.filter((rx) =>
    rx.test(fullText),
  ).length;

  if (pages.length === 1) {
    if (fullMarkerCount >= 4 && detailChars >= 80) {
      return { valid: true, pagesIncluded };
    }
    return {
      valid: false,
      error:
        'Detailed consumption pages could not be extracted from the report.',
      pagesIncluded,
    };
  }

  if (detailChars < 40) {
    return {
      valid: false,
      error:
        'Pages 2 onward contain no readable consumption data. Cannot generate insights from the cover page only.',
      pagesIncluded,
    };
  }

  if (detailMarkerCount < 3) {
    return {
      valid: false,
      error:
        'Could not extract Idle, Cargo Operations, Manoeuvring, TCP, or Technical Baseline data from consumption pages.',
      pagesIncluded,
    };
  }

  return { valid: true, pagesIncluded };
}

export async function extractTextFromPdfFile(file: File): Promise<string> {
  const pages = await extractTextByPageFromPdfBlob(file);
  if (pages.some((p) => p.rawText.trim())) {
    return formatPortConsumptionPagesContext(pages);
  }

  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    return '';
  }

  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const parts: string[] = [];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    const line = content.items
      .map((item) => item.str ?? '')
      .join(' ')
      .trim();
    if (line) {
      parts.push(line);
    }
  }

  return parts.join('\n');
}

export interface PdfPositionedTextItem {
  str: string;
  x: number;
  y: number;
  pageNumber?: number;
}

export interface PdfPageText {
  pageNumber: number;
  lines: string[];
  rawText: string;
}

export interface PortConsumptionExtractionMeta {
  pageCount: number;
  pagesIncluded: number[];
  textChars: number;
  usedOcr: boolean;
  markerCount: number;
}

export interface PortConsumptionReportExtraction {
  imageFile: File;
  extractedContext: string;
  pageCount: number;
  pagesIncluded: number[];
  extractionMeta: PortConsumptionExtractionMeta;
}

const PORT_CONSUMPTION_DETAIL_MARKERS = [
  /idle/i,
  /cargo/i,
  /manoeuvr|maneuver/i,
  /tcp/i,
  /baseline/i,
  /consumption/i,
  /benchmark/i,
];

function groupPositionedItemsIntoLines(
  items: PdfPositionedTextItem[],
  yTol = 5,
): string[] {
  if (!items.length) {
    return [];
  }
  const sorted = [...items].sort((a, b) => b.y - a.y);
  const lines: string[] = [];
  let current: PdfPositionedTextItem[] = [];

  for (const item of sorted) {
    if (current.length && Math.abs(current[0].y - item.y) > yTol) {
      current.sort((a, b) => a.x - b.x);
      lines.push(current.map((c) => c.str).join(' ').trim());
      current = [];
    }
    current.push(item);
  }
  if (current.length) {
    current.sort((a, b) => a.x - b.x);
    lines.push(current.map((c) => c.str).join(' ').trim());
  }
  return lines.filter(Boolean);
}

async function extractLinesFromPdfPage(page: PdfPage): Promise<string[]> {
  const content = await page.getTextContent();
  const items: PdfPositionedTextItem[] = [];

  for (const raw of content.items) {
    if (!('str' in raw) || !(raw as { str?: string }).str?.trim()) {
      continue;
    }
    const item = raw as { str: string; transform?: number[] };
    const t = item.transform ?? [1, 0, 0, 1, 0, 0];
    items.push({ str: item.str.trim(), x: t[4], y: t[5] });
  }

  return groupPositionedItemsIntoLines(items);
}

/** Text items with x/y for table-style PDF extraction (e.g. Heat Plan reports). */
export async function extractPositionedTextFromPdfBlob(
  blob: Blob,
): Promise<PdfPositionedTextItem[]> {
  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    return [];
  }

  const buffer = await blob.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const items: PdfPositionedTextItem[] = [];

  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();

    for (const raw of content.items) {
      if (!('str' in raw) || !(raw as { str?: string }).str?.trim()) {
        continue;
      }
      const item = raw as { str: string; transform?: number[] };
      const t = item.transform ?? [1, 0, 0, 1, 0, 0];
      items.push({ str: item.str.trim(), x: t[4], y: t[5] });
    }
  }

  return items;
}

export async function pdfPageAsImageFile(
  file: File,
  pageIndex = 1,
  compress = true,
  preferredScale = 2,
): Promise<File> {
  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    throw new Error('PDF.js not available');
  }

  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const page = await doc.getPage(Math.min(pageIndex, doc.numPages));
  const scale = resolvePdfRenderScale(page, preferredScale);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas not supported');
  }

  await page.render({ canvasContext: ctx, viewport }).promise;

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not render PDF page'))),
      'image/png',
    );
  });

  const base = file.name.replace(/\.pdf$/i, '') || 'document';
  const pngFile = new File([blob], `${base}-p${pageIndex}.png`, {
    type: 'image/png',
  });
  return compress ? compressImageFileForUpload(pngFile) : pngFile;
}

function resolveConsumptionPageNumbers(totalPages: number): number[] {
  if (totalPages <= 1) {
    return [1];
  }
  const maxDetailPages = 5;
  return Array.from(
    { length: Math.min(totalPages - 1, maxDetailPages) },
    (_, i) => i + 2,
  );
}

/** Stack selected PDF pages into one composite image for AI analysis. */
export async function pdfPagesAsCompositeImageFile(
  file: File,
  pageNumbers?: number[],
  maxTotalHeight = 7000,
  pageMaxWidth = 1000,
): Promise<File> {
  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    throw new Error('PDF.js not available');
  }

  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const pagesToRender =
    pageNumbers?.length && pageNumbers.length > 0
      ? pageNumbers.filter((n) => n >= 1 && n <= doc.numPages)
      : resolveConsumptionPageNumbers(doc.numPages);
  const pageCanvases: HTMLCanvasElement[] = [];

  for (const pageNum of pagesToRender) {
    const page = await doc.getPage(pageNum);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(1.2, pageMaxWidth / Math.max(baseViewport.width, 1));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      continue;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    ctx.fillStyle = '#111827';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillText(`Page ${pageNum}`, 12, 24);
    pageCanvases.push(canvas);
  }

  if (!pageCanvases.length) {
    throw new Error('Could not render any PDF pages.');
  }

  const gap = 12;
  const width = Math.max(...pageCanvases.map((c) => c.width));
  const totalHeight =
    pageCanvases.reduce((sum, canvas) => sum + canvas.height, 0) +
    gap * Math.max(0, pageCanvases.length - 1);
  const compositeScale =
    totalHeight > maxTotalHeight ? maxTotalHeight / totalHeight : 1;
  const finalWidth = Math.max(1, Math.round(width * compositeScale));
  const finalHeight = Math.max(1, Math.round(totalHeight * compositeScale));

  const composite = document.createElement('canvas');
  composite.width = finalWidth;
  composite.height = finalHeight;
  const compositeCtx = composite.getContext('2d');
  if (!compositeCtx) {
    throw new Error('Canvas not supported');
  }

  compositeCtx.fillStyle = '#ffffff';
  compositeCtx.fillRect(0, 0, finalWidth, finalHeight);

  let y = 0;
  for (const pageCanvas of pageCanvases) {
    const drawWidth = Math.round(pageCanvas.width * compositeScale);
    const drawHeight = Math.round(pageCanvas.height * compositeScale);
    compositeCtx.drawImage(pageCanvas, 0, y, drawWidth, drawHeight);
    y += drawHeight + Math.round(gap * compositeScale);
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    composite.toBlob(
      (b) =>
        b ? resolve(b) : reject(new Error('Could not render composite PDF image')),
      'image/jpeg',
      0.75,
    );
  });

  const base = file.name.replace(/\.pdf$/i, '') || 'report';
  return new File([blob], `${base}-consumption-pages.jpg`, {
    type: 'image/jpeg',
  });
}

/** @deprecated Use pdfPagesAsCompositeImageFile */
export async function pdfAllPagesAsCompositeImageFile(
  file: File,
): Promise<File> {
  return pdfPagesAsCompositeImageFile(file);
}

/** Returns rendered pixel size of an image file (0×0 when unreadable). */
export async function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp|tif?f)$/i.test(file.name);
  if (!isImage || !file.size) {
    return { width: 0, height: 0 };
  }

  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return { width: 0, height: 0 };
  }
}

export async function ensureMinImageDimensions(
  file: File,
  minWidth = 120,
  minHeight = 120,
): Promise<{ ok: boolean; width: number; height: number }> {
  const { width, height } = await getImageDimensions(file);
  return {
    ok: width >= minWidth && height >= minHeight,
    width,
    height,
  };
}

/** Enhance table screenshots for OCR: upscale, grayscale, contrast, white background. */
export async function preprocessImageForOcr(
  file: File,
  minWidth = 1400,
): Promise<File> {
  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp|tif?f)$/i.test(file.name);
  if (!isImage || !file.size) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = bitmap.width < minWidth ? minWidth / bitmap.width : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray =
        0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const contrast = 1.35;
      const adjusted = Math.max(
        0,
        Math.min(255, (gray - 128) * contrast + 128),
      );
      data[i] = adjusted;
      data[i + 1] = adjusted;
      data[i + 2] = adjusted;
    }
    ctx.putImageData(imageData, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
    if (!blob?.size) {
      return file;
    }

    const base = file.name.replace(/\.[^.]+$/i, '') || 'table';
    return new File([blob], `${base}-ocr.png`, { type: 'image/png' });
  } catch {
    return file;
  }
}

/** Rotate an image file by the given degrees (90, 180, 270). */
export async function rotateImageFile(file: File, degrees: 90 | 180 | 270): Promise<File> {
  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp|tif?f)$/i.test(file.name);
  if (!isImage || !file.size) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const radians = (degrees * Math.PI) / 180;
    const swap = degrees === 90 || degrees === 270;
    const width = swap ? bitmap.height : bitmap.width;
    const height = swap ? bitmap.width : bitmap.height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.translate(width / 2, height / 2);
    ctx.rotate(radians);
    ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((value) => resolve(value), 'image/png');
    });
    if (!blob?.size) {
      return file;
    }

    const base = file.name.replace(/\.[^.]+$/i, '') || 'table';
    return new File([blob], `${base}-rot${degrees}.png`, { type: 'image/png' });
  } catch {
    return file;
  }
}

/** Original plus 90/180/270° variants for robust table OCR/vision on rotated uploads. */
export async function createHeatingTableImageVariants(file: File): Promise<File[]> {
  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp|tif?f)$/i.test(file.name);
  if (!isImage || !file.size) {
    return [file];
  }

  const variants: File[] = [file];
  for (const degrees of [90, 180, 270] as const) {
    try {
      variants.push(await rotateImageFile(file, degrees));
    } catch {
      /* skip rotation */
    }
  }
  return variants;
}

async function extractTextFromSpreadsheetFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const lines: string[] = [];

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      continue;
    }
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });
    for (const row of rows) {
      if (!Array.isArray(row)) {
        continue;
      }
      const cells = row
        .map((cell) => String(cell ?? '').trim())
        .filter((cell) => cell.length > 0);
      if (cells.length) {
        lines.push(cells.join('\t'));
      }
    }
  }

  return lines.join('\n');
}

/** Resize/compress report images to keep vessel-insights uploads reliable. */
export async function compressImageFileForUpload(
  file: File,
  maxWidth = 1800,
): Promise<File> {
  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(file.name);
  if (!isImage || !file.size) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale =
      bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.82);
    });
    if (!blob?.size) {
      return file;
    }

    const base = file.name.replace(/\.[^.]+$/i, '') || 'report';
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

export async function pdfFirstPageAsImageFile(
  file: File,
  pageIndex = 1,
): Promise<File> {
  return pdfPageAsImageFile(file, pageIndex);
}

/** High-resolution PNG for vision models (no JPEG compression). */
export async function pdfPageAsVisionImageFile(
  file: File,
  pageIndex = 1,
): Promise<File> {
  return pdfPageAsImageFile(file, pageIndex, false, 3);
}

/**
 * Extract all port consumption report pages before AI analysis.
 * Merges per-page text and a composite multi-page image; fails if detail pages are missing.
 */
export async function preparePortConsumptionReportForAnalysis(
  pdfBlob: Blob,
  fileName = 'port-consumption-report.pdf',
): Promise<PortConsumptionReportExtraction> {
  const serverError = await readBlobErrorMessage(pdfBlob);
  if (serverError) {
    throw new Error(serverError);
  }
  if (!(await isPdfBlob(pdfBlob))) {
    throw new Error('Downloaded report is not a valid PDF.');
  }

  const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
  let pages = await extractTextByPageFromPdfBlob(pdfBlob);
  let validation = validatePortConsumptionExtraction(pages);
  let usedOcr = false;

  if (!validation.valid) {
    pages = await ocrSparseDetailPagesOnly(pdfFile, pages);
    usedOcr = true;
    validation = validatePortConsumptionExtraction(pages);
  }

  if (!validation.valid) {
    throw new Error(
      validation.error ||
        'Detailed consumption pages could not be extracted from the report.',
    );
  }

  const extractedContext = truncateExtractedReportContext(
    formatPortConsumptionPagesContext(pages),
  );
  const imageFile = await pdfPagesAsCompositeImageFile(pdfFile);
  const isImage =
    imageFile.type.startsWith('image/') ||
    /\.(png|jpe?g|webp)$/i.test(imageFile.name);

  if (!isImage || !imageFile.size) {
    throw new Error('Could not convert report pages to image for analysis.');
  }

  const markerCount = PORT_CONSUMPTION_DETAIL_MARKERS.filter((rx) =>
    rx.test(extractedContext),
  ).length;

  return {
    imageFile,
    extractedContext,
    pageCount: pages.length,
    pagesIncluded: validation.pagesIncluded,
    extractionMeta: {
      pageCount: pages.length,
      pagesIncluded: validation.pagesIncluded,
      textChars: extractedContext.replace(/\s/g, '').length,
      usedOcr,
      markerCount,
    },
  };
}

/** OCR every PDF page when the text layer is empty (scanned PDFs). */
export async function extractTextFromPdfViaOcr(file: File): Promise<string> {
  await loadPdfJs();
  const pdfjs = window.pdfjsLib;
  if (!pdfjs) {
    return '';
  }

  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const parts: string[] = [];
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    try {
      const pageImg = await pdfPageAsImageFile(file, pageNum);
      const text = await extractTextFromBoilerGraphImage(pageImg);
      if (text.trim()) {
        parts.push(text);
      }
    } catch {
      /* skip page */
    }
  }

  return parts.join('\n');
}

/**
 * Read any supported upload as plain text (PDF text layer, OCR, or file body).
 */
export async function extractTextFromUpload(file: File): Promise<string> {
  const kind = classifyUploadFile(file);

  if (kind === 'pdf') {
    const embedded = await extractTextFromPdfFile(file);
    if (embedded.trim().length > 15) {
      return embedded;
    }
    const ocr = await extractTextFromPdfViaOcr(file);
    return ocr || embedded;
  }

  if (kind === 'image') {
    try {
      const prepared = await preprocessImageForOcr(
        await compressImageFileForUpload(file, 2200),
      );
      return await extractTextFromBoilerGraphImage(prepared);
    } catch {
      return '';
    }
  }

  if (kind === 'structured') {
    return readFileAsText(file);
  }

  if (kind === 'spreadsheet') {
    try {
      return await extractTextFromSpreadsheetFile(file);
    } catch {
      return readFileAsText(file);
    }
  }

  const asText = await readFileAsText(file);
  if (asText.trim().length > 10) {
    return asText;
  }

  if (isDocumentImageFile(file)) {
    try {
      const prepared = await preprocessImageForOcr(
        await compressImageFileForUpload(file, 2200),
      );
      return await extractTextFromBoilerGraphImage(prepared);
    } catch {
      return '';
    }
  }

  return '';
}

/** @deprecated Use isDocumentImageFile */
export function isImageUploadFile(file: File): boolean {
  return isDocumentImageFile(file);
}
